/**
 * WebSocket Context for Real-time Chat
 * 
 * Provides WebSocket connection to the entire app.
 * Use this context to access real-time chat features.
 */

import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { useWebSocket, WS_STATE } from '@/hooks/useWebSocket';
import { useAuth } from '@/contexts/AuthContext';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const ws = useWebSocket();
  
  // Track unread messages per match
  const [unreadCounts, setUnreadCounts] = useState({});
  
  // Handle incoming messages to update unread counts
  useEffect(() => {
    if (!ws.isConnected) return;
    
    const unsubMessage = ws.onMessage('message', (data) => {
      const message = data.message;
      // If message is from someone else, increment unread
      if (message && message.sender_id !== user?.id) {
        setUnreadCounts(prev => ({
          ...prev,
          [message.match_id]: (prev[message.match_id] || 0) + 1
        }));
      }
    });
    
    const unsubRead = ws.onMessage('read', (data) => {
      // Clear unread when messages are read
      if (data.match_id) {
        setUnreadCounts(prev => ({
          ...prev,
          [data.match_id]: 0
        }));
      }
    });
    
    return () => {
      unsubMessage();
      unsubRead();
    };
  }, [ws.isConnected, ws.onMessage, user?.id]);
  
  // Clear unread for a specific match
  const clearUnread = useCallback((matchId) => {
    setUnreadCounts(prev => {
      if (!prev[matchId]) return prev;
      return { ...prev, [matchId]: 0 };
    });
    // Also send read receipt
    ws.sendReadReceipt(matchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.sendReadReceipt]);
  
  // Get total unread count
  const totalUnread = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);

  const value = {
    ...ws,
    unreadCounts,
    clearUnread,
    totalUnread,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
};

// Re-export WS_STATE for convenience
export { WS_STATE };

export default WebSocketContext;
